// AuthProvider.js
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { signOut } from "firebase/auth";

const AuthContext = createContext({
  user: null,
  userInfo: null,
  loading: true,
});

// Espejo en utils/usoPlataforma.js (ROLES_DEL_PANEL).
const ALLOWED_ROLES = ["admin", "owner", "viewer", "inversionista"];

export const AuthProvider = ({ children }) => {
  const [user, loading] = useAuthState(auth);
  const [userInfo, setUserInfo] = useState(() => {
    const saved = localStorage.getItem("userInfo");
    return saved ? JSON.parse(saved) : null;
  });
  const [roleLoading, setRoleLoading] = useState(true);

  useEffect(() => {
    if (loading) {
      setRoleLoading(true);
      return;
    }
    if (!user?.uid) {
      setUserInfo(null);
      setRoleLoading(false);
      return;
    }

    setRoleLoading(true);
    const checkUserRole = async () => {
      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        // Check if the user exists and has one of the allowed roles
        if (!userDoc.exists() || !ALLOWED_ROLES.includes(userDoc.data().role)) {
          await signOut(auth);
          setUserInfo(null);
          return;
        }
        setUserInfo(userDoc.data());
      } catch (error) {
        console.error("Error fetching user info:", error);
        await signOut(auth);
        setUserInfo(null);
      }
      setRoleLoading(false);
    };

    checkUserRole();
  }, [user?.uid, loading]);

  useEffect(() => {
    if (userInfo) {
      localStorage.setItem("userInfo", JSON.stringify(userInfo));
    } else {
      localStorage.removeItem("userInfo");
    }
  }, [userInfo]);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      user,
      userInfo,
      setUserInfo,
      loading: loading || roleLoading,
    }),
    [user, userInfo, loading, roleLoading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
